import {
  CONFIG_DEFAULTS,
  CONFIG_KEYS,
  ConfigurationKey,
  ConfigurationMap,
  ConfigurationValue,
  parseConfigurationValue,
} from "@salon/types/configuration";

/**
 * Parse a single configuration value with type inference
 */
export function parseConfiguration<K extends ConfigurationKey>(
  key: K,
  value: string | undefined
): ConfigurationValue<K> {
  if (!value) {
    return CONFIG_DEFAULTS[key];
  }
  return parseConfigurationValue(key, value);
}

/**
 * Parse multiple configuration values with type inference
 */
export function parseConfigurations<K extends ConfigurationKey>(
  configMap: Record<string, string>,
  keys: readonly K[]
): ConfigurationMap<K> {
  const result = {} as ConfigurationMap<K>;

  for (const key of keys) {
    result[key] = parseConfiguration(key, configMap[key]);
  }

  return result;
}

/**
 * Parse all configurations from a GraphQL response
 */
export function parseConfigurationResponse(
  configurations: Array<{
    key: string;
    value: string;
    dataType: string;
    category: string;
  }>
): Record<string, unknown> {
  const configMap: Record<string, string> = {};

  // Convert array to map
  for (const config of configurations) {
    configMap[config.key] = config.value;
  }

  // Parse using all known configuration keys
  const allKeys = Object.values(CONFIG_KEYS).flatMap((category) =>
    Object.values(category)
  ) as ConfigurationKey[];

  return parseConfigurations(configMap, allKeys);
}

/**
 * Get a typed configuration value from a config map
 */
export function getConfigurationValue<K extends ConfigurationKey>(
  configMap: { key: string; value: string }[],
  key: K
): ConfigurationValue<K> {
  return parseConfiguration(key, configMap.find((c) => c.key === key)?.value);
}

// Export shared CONFIG_KEYS for convenience
export { CONFIG_KEYS };
